import { SoundSwitch } from "../components/juice";
import { InstallButton } from "../components/InstallButton";
import { useEffect, useState } from "react";
import { LuArrowRight, LuSwords } from "react-icons/lu";
import { GAME_IDS, GRADES, gradeLabel } from "../../shared/constants";
import { ApiError, get, post } from "../api";
import { type Equipped } from "../components/Avatar";
import { Floaties, Sparkles } from "../components/fx";
import { Figure } from "../components/Figure";
import { HeroScene } from "../components/HeroScene";
import { GameShowcase, SLOGAN, SiteFooter } from "../components/Showcase";
import { Field, LogoMark } from "../components/ui";
import { Link, navigate } from "../router";
import { useApp, type Me } from "../store";

/** the frame of the log-in, sign-up and welcome pages: the same drawn sky with floating coins and cubes behind a clear card */
function AuthShell({ width, children }: { width: number; children: any }) {
  return (
    <div className="auth-shell" data-spot>
      <div className="auth-bg" aria-hidden="true">
        <HeroScene palette="dusk" scene="castles" />
        <Sparkles n={26} />
        <Floaties kinds={["coin", "cube", "star", "gem"]} n={9} seed={3} />
      </div>
      <div className="main auth-main" style={{ maxWidth: width }}>{children}</div>
    </div>
  );
}

export function Landing({ offline }: { offline?: boolean }) {
  return (
    <div className="main" style={{ maxWidth: 980 }}>
      <a href="#main" className="skip">Spring til indhold</a>
      <header className="row between" style={{ marginBottom: 18 }}>
        <span className="brand" style={{ padding: 0 }}>
          <span className="logo" aria-hidden="true"><LogoMark /></span> EduBattle
        </span>
        <span className="row">
          <SoundSwitch />
          <InstallButton />
          <Link to="/hjaelp" className="btn ghost small">Hjælp</Link>
          <Link to="/login" className="btn ghost small">Log ind</Link>
        </span>
      </header>
      {offline && <div className="notice warn" role="status">Du er offline. EduBattle kræver internet for at spille.</div>}
      <section className="home-hero landing" id="main" tabIndex={-1} aria-labelledby="land-h">
        <HeroScene />
        <Sparkles n={26} />
        <div className="hh-inner single">
          <div className="hh-center">
            <span className="land-brand" aria-hidden="true"><span className="logo"><LogoMark /></span> EduBattle</span>
            <h1 id="land-h" className="title-game hh-title land-slogan"><span className="sr-only">EduBattle – </span>{SLOGAN}</h1>
            <p className="hh-sub wide">
              {GAME_IDS.length} spil · 0.–9. klasse · gratis
            </p>
            <Link to="/opret" className="bigplay">
              <span className="bp-ico"><LuSwords aria-hidden="true" size={36} /></span>
              <span className="bp-text"><b>Opret gratis konto</b><small>Det tager et minut</small></span>
              <LuArrowRight className="bp-arrow" aria-hidden="true" size={30} />
            </Link>
            <Link to="/login" className="btn ghost">Jeg har en konto</Link>
          </div>
        </div>
      </section>
      <GameShowcase />
      <section className="sc-final" data-reveal aria-label="Kom i gang">
        <h2 className="sc-title">Klar til at spille?</h2>
        <p>Gratis – og du er i gang på et minut.</p>
        <Link to="/opret" className="bigplay">
          <span className="bp-ico"><LuSwords aria-hidden="true" size={36} /></span>
          <span className="bp-text"><b>Opret gratis konto</b><small>{SLOGAN}</small></span>
          <LuArrowRight className="bp-arrow" aria-hidden="true" size={30} />
        </Link>
      </section>
      <SiteFooter />
    </div>
  );
}

export function Login() {
  const { setMe, toast } = useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: any) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await post<{ user: Me }>("/api/auth/login", { username, password });
      setMe(r.user);
      navigate("/");
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Noget gik galt.");
      toast("Login mislykkedes", "error");
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthShell width={460}>
      <Link to="/" className="brand"><span className="logo" aria-hidden="true"><LogoMark /></span> EduBattle</Link>
      <form className="card stack authcard" onSubmit={submit}>
        <h1>Log ind</h1>
        <Field label="Brugernavn"><input value={username} onChange={(e: any) => setUsername(e.target.value)} autoComplete="username" required autoFocus /></Field>
        <Field label="Adgangskode"><input type="password" value={password} onChange={(e: any) => setPassword(e.target.value)} autoComplete="current-password" required /></Field>
        {err && <div className="err" role="alert">{err}</div>}
        <button className="btn" disabled={busy}>{busy ? "Logger ind…" : "Log ind"}</button>
        <p className="muted">Glemt din kode? Bed din lærer om en midlertidig adgangskode.</p>
        <p>Ny her? <Link to="/opret">Opret en konto</Link></p>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  const { setMe } = useApp();
  const [role, setRole] = useState<"student" | "teacher">("student");
  const [f, setF] = useState({ realName: "", age: "", grade: "", username: "", password: "", teacherCode: "" });
  const [guardianOk, setGuardianOk] = useState(false);
  const [uc, setUc] = useState<{ available?: boolean; error?: string } | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const age = Number(f.age);
  const set = (k: keyof typeof f) => (e: any) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (f.username.length < 3) return setUc(null);
    const t = setTimeout(async () => {
      try {
        setUc(await get(`/api/auth/username-check?u=${encodeURIComponent(f.username)}`));
      } catch {
        setUc(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [f.username]);

  async function submit(e: any) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await post<{ user: Me }>("/api/auth/register", {
        role,
        realName: f.realName,
        age: age,
        grade: role === "student" ? Number(f.grade) : null,
        username: f.username,
        password: f.password,
        guardianOk,
        ...(role === "teacher" ? { teacherCode: f.teacherCode } : {}),
      });
      setMe(r.user);
      navigate(role === "student" ? "/velkommen" : "/laerer");
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Noget gik galt.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthShell width={560}>
      <Link to="/" className="brand"><span className="logo" aria-hidden="true"><LogoMark /></span> EduBattle</Link>
      <form className="card stack authcard" onSubmit={submit}>
        <h1>Opret konto</h1>
        <div className="segment" role="group" aria-label="Jeg er">
          <button type="button" aria-pressed={role === "student"} onClick={() => setRole("student")}>Elev</button>
          <button type="button" aria-pressed={role === "teacher"} onClick={() => setRole("teacher")}>Lærer</button>
        </div>
        <div className="notice">
          <strong>Dit privatliv:</strong> Dit rigtige navn og din alder bruges kun til din konto og vises <em>aldrig</em> for andre spillere. Andre ser kun dit <strong>brugernavn</strong> og din avatar. Vi gemmer ikke din præcise alder – kun et aldersinterval.
        </div>
        <Field label="Dit rigtige navn" hint="Bliver ikke vist for andre"><input value={f.realName} onChange={set("realName")} autoComplete="name" required maxLength={80} /></Field>
        <div className="grid cols-2">
          <Field label="Din alder"><input type="number" min={4} max={120} value={f.age} onChange={set("age")} required inputMode="numeric" /></Field>
          {role === "student" && (
            <Field label="Dit klassetrin">
              <select value={f.grade} onChange={set("grade")} required>
                <option value="">Vælg…</option>
                {GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
              </select>
            </Field>
          )}
        </div>
        <Field label="Vælg et brugernavn" hint="3-16 tegn. Brug ikke dit rigtige navn, e-mail eller telefonnummer.">
          <input value={f.username} onChange={set("username")} autoComplete="off" required maxLength={16} aria-describedby="uname-status" />
        </Field>
        <div id="uname-status" aria-live="polite" className={uc?.available ? "okmsg" : "err"}>
          {uc?.available ? "✓ Brugernavnet er ledigt" : uc?.error ?? ""}
        </div>
        <Field label="Adgangskode" hint="Mindst 8 tegn"><input type="password" value={f.password} onChange={set("password")} autoComplete="new-password" required minLength={8} /></Field>
        {role === "teacher" && <Field label="Skolekode" hint="Lærerkonti oprettes kun med en kode fra din skole/administrator"><input value={f.teacherCode} onChange={set("teacherCode")} autoComplete="off" required /></Field>}
        {role === "student" && age > 0 && age < 13 && (
          <label className="check">
            <input type="checkbox" checked={guardianOk} onChange={(e: any) => setGuardianOk(e.target.checked)} />
            <span>Jeg er under 13 år, og en forælder eller min skole har givet mig lov til at oprette en konto.</span>
          </label>
        )}
        {err && <div className="err" role="alert">{err}</div>}
        <button className="btn lime" disabled={busy || (uc ? uc.available === false : false)}>{busy ? "Opretter…" : "Opret konto"}</button>
        <p>Har du allerede en konto? <Link to="/login">Log ind</Link></p>
      </form>
    </AuthShell>
  );
}

const STARTER: { skin: string; label: string }[] = [
  { skin: "skin_kid_dreng", label: "Mikkel" },
  { skin: "skin_kid_pige", label: "Maja" },
  { skin: "skin_pjat", label: "Pjat" },
  { skin: "skin_blip", label: "Blip" },
  { skin: "skin_rask", label: "Rask" },
];
/** the free starter figures – the same data as in the catalogue (src/server/seed/cosmetics.ts) so the picture is right before anything is saved */
const SKINS: Record<string, any> = {
  skin_kid_dreng: { id: "skin_kid_dreng", data: { species: "kid", body: "#f5c096", eyeColor: "#4a2b17", accent: "#1b2a6b", hair: "spiky", hairColor: "#6b3f22", outfit: { kind: "jacket", top: "#26357a", bottom: "#33415c", trim: "#ffd166", shoes: "#f1f5f9", emblem: "star" } } },
  skin_kid_pige: { id: "skin_kid_pige", data: { species: "kid", body: "#f1c27d", eyeColor: "#2f6b3a", accent: "#be185d", hair: "long", hairColor: "#3b2417", outfit: { kind: "dress", top: "#ec4899", bottom: "#ec4899", trim: "#fde047", shoes: "#fbcfe8" } } },
  skin_pjat: { id: "skin_pjat", data: { species: "blob", body: "#4cc9f0", accent: "#1b2a6b", belly: "#c8f3ff" } },
  skin_blip: { id: "skin_blip", data: { species: "frog", body: "#7CFC8A", accent: "#14532d", belly: "#eaffd0", size: 0.92 } },
  skin_rask: { id: "skin_rask", data: { species: "fox", body: "#ff9f43", accent: "#7c2d12", belly: "#fff4e6" } },
};
const HATS: Record<string, any> = {
  hat_kasket: { id: "hat_kasket", data: { kind: "cap", color: "#ef4444" } },
  hat_haarbaand: { id: "hat_haarbaand", data: { kind: "headband", color: "#22d3ee" } },
};

export function Welcome() {
  const { me, reload } = useApp();
  const [step, setStep] = useState(me!.onboardingStep <= 1 ? 1 : 2);
  const [skin, setSkin] = useState("skin_kid_dreng");
  const [hat, setHat] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preview: Equipped = { ...(me!.avatar as Equipped), skin: SKINS[skin], ...(hat ? { hat: HATS[hat] } : {}) };
  async function saveAvatar() {
    setBusy(true);
    try {
      await post("/api/inventory/equip", { id: skin });
      if (hat) await post("/api/inventory/equip", { id: hat });
      await post("/api/me/onboarding", { step: 2 });
      await reload();
      setStep(2);
    } finally {
      setBusy(false);
    }
  }
  async function finish() {
    await post("/api/me/onboarding", { step: 3 });
    await reload();
    navigate("/");
  }
  return (
    <AuthShell width={760}>
      <h1>Velkommen, {me!.username}!</h1>
      <p className="muted">Trin {step} af 2</p>
      {step === 1 ? (
        <div className="card stack">
          <h2>Vælg din figur</h2>
          <div className="figure-stage" style={{ height: 320 }}>
            <Figure equipped={preview} framing="hero" label="Din figur – træk for at dreje den, klik for at få den til at reagere" />
          </div>
          <p className="muted center" style={{ margin: 0 }}>Træk for at dreje figuren – klik på den og se, hvad der sker!</p>
          <div className="row" role="group" aria-label="Figurer" style={{ justifyContent: "center" }}>
            {STARTER.map((s) => (
              <button key={s.skin} className="tab" aria-pressed={skin === s.skin} aria-selected={skin === s.skin} onClick={() => setSkin(s.skin)}>{s.label}</button>
            ))}
          </div>
          <div className="row" role="group" aria-label="Hatte" style={{ justifyContent: "center" }}>
            <button className="tab" aria-selected={hat === null} onClick={() => setHat(null)}>Ingen hat</button>
            <button className="tab" aria-selected={hat === "hat_kasket"} onClick={() => setHat("hat_kasket")}>Kasket</button>
            <button className="tab" aria-selected={hat === "hat_haarbaand"} onClick={() => setHat("hat_haarbaand")}>Hovedbånd</button>
          </div>
          <p className="muted center">Mere pynt kan købes i butikken senere.</p>
          <button className="btn lime" onClick={saveAvatar} disabled={busy}>Næste</button>
        </div>
      ) : (
        <div className="card stack">
          <h2>Sådan virker EduBattle</h2>
          <div className="grid cols-3">
            <div className="card flat"><h3>1. Vælg et spil</h3><p className="muted">Alene, mod bot eller ven.</p></div>
            <div className="card flat"><h3>2. Svar for at spille</h3><p className="muted">Rigtigt svar = du må spille.</p></div>
            <div className="card flat"><h3>3. Tjen og pynt</h3><p className="muted">Mønter får du ved at spille.</p></div>
          </div>
          <button className="btn lime" onClick={finish}>Start!</button>
        </div>
      )}
    </AuthShell>
  );
}
